import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Autocomplete, TextField } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import { getClientFollowUpCalendar } from '../../../utils/api';
import type { CalendarAction, ClientActionStatus } from '../../../utils/clientFollowUp';
import {
  assignedUserLabel,
  assigneeOptions,
  buildMonthGrid,
  CLIENT_ACTION_STATUS_CLASSES,
  CLIENT_ACTION_STATUS_LABELS,
  CLIENT_ACTION_TYPES,
  filterCalendarActions,
  formatActionDay,
  groupActionsByDay,
  RECURRING_ACTION_TYPE,
  todayKey,
} from '../../../utils/clientFollowUp';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';

const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const VISIBLE_ACTIONS_PER_DAY = 3;
const LEGEND: ClientActionStatus[] = ['overdue', 'soon', 'upcoming', 'recurring'];

const actionDetails = (action: CalendarAction): string => [
  action.title,
  CLIENT_ACTION_STATUS_LABELS[action.status],
  assignedUserLabel(action.assignedUser),
].join(' — ');

function ActionLink({ action }: { action: CalendarAction }) {
  return (
    <a
      href={ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', action.companyId.toString())}
      target="_blank"
      rel="noreferrer"
      title={actionDetails(action)}
      className={`block truncate rounded px-1.5 py-0.5 text-xs font-medium hover:underline ${CLIENT_ACTION_STATUS_CLASSES[action.status]}`}
    >
      {action.eventName ?? 'DPE'} · {action.companyName}
    </a>
  );
}

function ClientsPlanning() {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [actions, setActions] = useState<CalendarAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<{ id: number; name: string } | null>(null);
  const [type, setType] = useState('');
  const [assignee, setAssignee] = useState('');
  const [openDay, setOpenDay] = useState<string | null>(null);
  const dayDetailsRef = useRef<HTMLDivElement>(null);

  const weeks = useMemo(() => buildMonthGrid(month.year, month.month), [month]);
  const from = weeks[0][0].day;
  const to = weeks[weeks.length - 1][6].day;
  const includeRecurring = type === RECURRING_ACTION_TYPE;

  useEffect(() => {
    if (!jwt) {return undefined;}
    let cancelled = false;
    setLoading(true);
    (async() => {
      try {
        const response = await fetch(getClientFollowUpCalendar(from, to, includeRecurring), JWTHeader(jwt));
        if (!response.ok) {throw new Error(`HTTP ${response.status}`);}
        const data: CalendarAction[] = await response.json();
        if (!cancelled) {setActions(data);}
      } catch (error) {
        console.error('Error fetching the clients planning:', error);
        if (!cancelled) {
          setActions([]);
          showToast('error', 'Une erreur est survenue lors du chargement du planning.');
        }
      } finally {
        if (!cancelled) {setLoading(false);}
      }
    })();
    return () => { cancelled = true; };
  }, [jwt, from, to, includeRecurring]);

  useEffect(() => {
    if (openDay) {
      dayDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [openDay]);

  const filteredActions = useMemo(
    () => filterCalendarActions(actions, { companyId: client?.id ?? null, type, assignee }),
    [actions, client, type, assignee],
  );
  const actionsByDay = useMemo(() => groupActionsByDay(filteredActions), [filteredActions]);

  const clientOptions = useMemo(() => {
    const clients = new Map<number, string>(client ? [[client.id, client.name]] : []);
    for (const action of actions) {
      clients.set(action.companyId, action.companyName);
    }
    return [...clients.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [actions, client]);

  const assignees = useMemo(() => assigneeOptions(actions), [actions]);

  const inMonthActions = useMemo(() => {
    const monthPrefix = `${month.year}-${String(month.month + 1).padStart(2, '0')}`;
    return filteredActions.filter((action) => action.date.startsWith(monthPrefix));
  }, [filteredActions, month]);
  const overdueCount = inMonthActions.filter((action) => action.status === 'overdue').length;

  const today = todayKey();
  const monthTitle = new Date(month.year, month.month, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const moveMonth = (offset: number) => {
    setOpenDay(null);
    setMonth((current) => {
      const target = new Date(current.year, current.month + offset, 1);
      return { year: target.getFullYear(), month: target.getMonth() };
    });
  };

  const goToToday = () => {
    const now = new Date();
    setOpenDay(null);
    setMonth({ year: now.getFullYear(), month: now.getMonth() });
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-md">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center text-2xl font-bold text-gray-800">
          <CalendarDaysIcon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
          Planning clients
        </h1>
        <Link to={ROUTES.ADMIN_CLIENTS_LIST} className="text-sm font-medium text-rgpd-blue-dark hover:underline">
          Voir le tableau des clients
        </Link>
      </div>

      <p className="mb-4 text-sm text-gray-600">
        Les actions du planning de tous les clients actifs. Une échéance marquée comme faite ou désactivée n'apparaît pas.
        Cliquez sur une action pour ouvrir la fiche du client.
      </p>

      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Autocomplete
          size="small"
          options={clientOptions}
          value={client}
          onChange={(_, value) => setClient(value)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="Aucun client ce mois-ci"
          renderInput={(params) => <TextField {...params} label="Client" />}
        />
        <div>
          <label htmlFor="planning-action-type" className="sr-only">Type d'action</label>
          <select
            id="planning-action-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-10 w-full rounded border border-gray-300 px-2"
          >
            <option value="">Tous les types (hors DPE)</option>
            {CLIENT_ACTION_TYPES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="planning-assignee" className="sr-only">Personne assignée</label>
          <select
            id="planning-assignee"
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            className="h-10 w-full rounded border border-gray-300 px-2"
          >
            <option value="">Toutes les personnes</option>
            {assignees.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => moveMonth(-1)} aria-label="Mois précédent" className="rounded border border-gray-300 p-1.5 hover:bg-gray-50">
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button type="button" onClick={() => moveMonth(1)} aria-label="Mois suivant" className="rounded border border-gray-300 p-1.5 hover:bg-gray-50">
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <button type="button" onClick={goToToday} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
            Aujourd'hui
          </button>
          <h2 className="ml-2 text-xl font-bold text-gray-800 first-letter:uppercase">{monthTitle}</h2>
          {loading && <Spinner size="sm" />}
        </div>
        {!loading && (
          <p className="text-sm text-gray-600">
            {inMonthActions.length} action{inMonthActions.length > 1 ? 's' : ''} ce mois-ci
            {overdueCount > 0 && `, dont ${overdueCount} en retard`}
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[56rem] rounded border border-gray-200">
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {WEEK_DAYS.map((weekDay) => (
              <div key={weekDay} className="px-2 py-1.5 text-center text-sm font-semibold text-gray-600">{weekDay}</div>
            ))}
          </div>
          {weeks.map((week) => (
            <div key={week[0].day} className="grid grid-cols-7 border-b border-gray-200 last:border-b-0">
              {week.map((day) => {
                const dayActions = actionsByDay.get(day.day) ?? [];
                const hiddenCount = dayActions.length - VISIBLE_ACTIONS_PER_DAY;
                return (
                  <div
                    key={day.day}
                    className={`min-h-28 min-w-0 border-r border-gray-200 p-1 last:border-r-0 ${day.inMonth ? '' : 'bg-gray-50'}`}
                  >
                    <div className={`mb-1 text-right text-xs ${day.day === today ? 'font-bold text-rgpd-blue-dark' : day.inMonth ? 'text-gray-700' : 'text-gray-400'}`}>
                      {day.dayOfMonth}
                    </div>
                    <div className="space-y-0.5">
                      {dayActions.slice(0, VISIBLE_ACTIONS_PER_DAY).map((action) => (
                        <ActionLink key={`${action.source}-${action.companyId}-${action.type}-${action.eventName}`} action={action} />
                      ))}
                      {hiddenCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setOpenDay(openDay === day.day ? null : day.day)}
                          className="w-full rounded px-1.5 py-0.5 text-left text-xs font-medium text-rgpd-blue-dark hover:bg-gray-100"
                        >
                          + {hiddenCount} autre{hiddenCount > 1 ? 's' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {openDay && (actionsByDay.get(openDay)?.length ?? 0) > 0 && (
        <div ref={dayDetailsRef} className="mt-4 rounded border border-gray-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Actions du {formatActionDay(openDay)}</h3>
            <button type="button" onClick={() => setOpenDay(null)} className="text-sm text-gray-600 hover:underline">Fermer</button>
          </div>
          <ul className="space-y-2">
            {(actionsByDay.get(openDay) ?? []).map((action) => (
              <li key={`${action.source}-${action.companyId}-${action.type}-${action.eventName}`} className="grid gap-1 text-sm md:grid-cols-3 md:items-center">
                <ActionLink action={action} />
                <span className="text-gray-700">{action.title}</span>
                <span className="text-gray-600">{assignedUserLabel(action.assignedUser)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
        {LEGEND.map((status) => (
          <span key={status} className={`rounded px-1.5 py-0.5 font-medium ${CLIENT_ACTION_STATUS_CLASSES[status]}`}>
            {CLIENT_ACTION_STATUS_LABELS[status]}
          </span>
        ))}
        <span>Le DPE revient chaque mercredi pour tous les clients : il ne s'affiche que si vous choisissez ce type.</span>
      </div>
    </div>
  );
}

export default ClientsPlanning;
